import clsx from 'clsx';
import { srsMapping } from '../../utils/mappings';
import GlassyBadge from '../common/GlassyBadge';

const SearchResultCard = ({ item, type, onClick, variant = 'default', compact = false, hideBadges = false, learnedIds }) => {
  if (!item) return null;

  // --- Card Data and Logic ---
  // Prefer live learnedIds set (from MetadataProvider) over stored item field to avoid stale state
  const isLearned = learnedIds ? learnedIds.has(item.id) : false;
  const isSkipped = item.skipped || false;
  const srsStage = isLearned ? (isSkipped ? 7 : item.srs_stage ?? 0) : 0;
  const nLevel = item.level ?? 0;

  // --- Dynamic Styling ---
  const srsLeftBorderClassMap = {
    0: 'border-l-srs-0',
    1: 'border-l-srs-1',
    2: 'border-l-srs-2',
    3: 'border-l-srs-3',
    4: 'border-l-srs-4',
    5: 'border-l-srs-5',
    6: 'border-l-srs-6',
    7: 'border-l-srs-7',
    8: 'border-l-srs-8',
    9: 'border-l-srs-9',
  };

  const leftBorderClass = isLearned ? srsLeftBorderClassMap[srsStage] : 'border-l-transparent';

  // Background variants for different contexts
  const getBackgroundClasses = () => {
    if (isLearned) {
      // All learned items get green background regardless of variant
      return 'bg-state-learned-bg';
    }

    // Unlearned items get contrasting background based on variant
    switch (variant) {
      case 'light':
        // Light variant: gray background for contrast against white
        return 'bg-background-primary';
      case 'dark':
        // Dark variant: lighter background for contrast against dark
        return 'bg-background-secondary';
      default:
        // Default variant: use your existing system
        return 'bg-background-secondary';
    }
  };

  const cardClasses = clsx(
    'w-full',
    'text-left',
    'rounded-lg',
    'flex',
    'items-center',
    'justify-between',
    'gap-4',
    'p-2.5',
    'border',
    'border-border',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-primary',
    'cursor-pointer',
    'border-l-4',
    'transition-all',
    'duration-200',
    'ease-out',
    // Hover effects - jump up and color change for all variants
    'hover:-translate-y-1',
    'hover:text-primary',
    leftBorderClass,
    getBackgroundClasses()
  );

  // --- Content Preparation ---
  const mainText = type === 'words' ? item.word : item.sentence;
  const translationText =
    type === 'words'
      ? item.senses?.[0]?.translation?.[0] || '' // Use senses for words (modern approach)
      : item.translation; // Keep translation for sentences

  return (
    <button onClick={onClick} className={cardClasses}>
      {/* --- Main Content (Left Side) --- */}
      <div className="flex-grow min-w-0">
        <p className="font-semibold truncate text-base" lang="ja">{mainText}</p>
        {!compact && <p className="text-sm text-content-secondary truncate">{translationText}</p>}
      </div>

      {/* --- Badges (Right Side) --- */}
      {!hideBadges && (
        <div className="flex flex-shrink-0 items-center gap-2">
          {isLearned && (
            <GlassyBadge srsStage={srsStage} size="sm" variant="solid" className={`sm:px-2.5 sm:py-1 ${compact ? 'hidden sm:inline-flex' : ''}`}>
              {srsMapping[srsStage]}
            </GlassyBadge>
          )}
          {type === 'sentences' && item.kanji_complexity != null && (
            <GlassyBadge color="blue" size="sm" variant="solid" className={`sm:px-2.5 sm:py-1 ${compact ? 'hidden sm:inline-flex' : ''}`}>
              {item.kanji_complexity === 61 ? '漢61+' : `漢${item.kanji_complexity}`}
            </GlassyBadge>
          )}
          <GlassyBadge level={nLevel} size="sm" variant="solid" className={`sm:px-2.5 sm:py-1 ${compact ? 'hidden sm:inline-flex' : ''}`}>
            N{nLevel}
          </GlassyBadge>
        </div>
      )}
    </button>
  );
};

export default SearchResultCard;
